import { Form, Formik } from "formik";
import React, { FC } from "react";
import Modal from 'react-modal';
import { toast } from "react-toastify";
import { useLocale } from "../../../contexts/localeContext";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import closeIcon from "../../../images/close.svg";
import { importCrlCertificate } from "../../../services/securityPolicyService";
import { getErrorMessage, showError } from '../../../services/utilities/ServiceError';
import { useDialog } from '../../dialog/DialogContext';
import ImportCrlContent from "./ImportCrlContent";

interface ImportCrlFileProps {
    isOpen: boolean;
    onClose: () => void;
    setBusy: React.Dispatch<React.SetStateAction<boolean>>
}

interface ImportCrlFileFormValues {
    pemCrlCertificate: string;
    pemCrlCertificateFileContent: number[];
    pemCaCertificate: string;
    caCertificateFileContent: number[];
    caLinkedChange: boolean;
}

const ImportCrlModal: FC<ImportCrlFileProps> = ({ isOpen, onClose, setBusy }) => {
    const { translate } = useLocale();
    const dialog = useDialog();
    const initialValues: ImportCrlFileFormValues = {
        pemCrlCertificate: '',
        pemCrlCertificateFileContent: [],
        pemCaCertificate: '',
        caCertificateFileContent: [],
        caLinkedChange: false,
    };

    /**
  * Allow the user to select the Broker service location.
  * @param event
  */
    const onCrlFileImport = async (values: any) => {
        try {
            setBusy(true);
            const caByteInfo = values.caCertificateFileContent;
            const crlByteInfo = values.pemCrlCertificateFileContent;
            const caName = values.pemCaCertificate;
            const crlName = values.pemCrlCertificate;
            const caLinked = values.caLinkedChange;

            if (!crlByteInfo) {
                dialog?.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.crlImportFileMissingError) });
                return;
            }

            if (caLinked && !caByteInfo) {
                dialog?.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.caLinkedCheckedFileMissingError) });
                return;
            }
            const crlFiles = [
                {
                    fileType: "CRL",
                    fileName: crlName,
                    fileContentBytes: crlByteInfo
                }
            ]
            if (caLinked) {
                crlFiles.push({ fileType: "CA", fileName: caName, fileContentBytes: caByteInfo })
            }
            const response = await importCrlCertificate(caLinked, crlFiles, translate);
            
            if (response.success) {
                toast.success(translate.byKey(TranslationKeys.success));
            }
            else {
                toast.error(translate.byKey(TranslationKeys.error));
            }
        }
        catch (error) {
            showError(error, toast.warn, getErrorMessage(error));
        }
        finally {
            setBusy(false); 
            onClose();
        }
    };

    return <Modal
        isOpen={isOpen}
        ariaHideApp={false}
        overlayClassName="flex w-screen position-float fixed top-0 left-0 h-screen bg-opacity-75 bg-white"
        className="bg-white border-none m-auto shadow select-none outline-none overflow-hidden" >
        <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={onCrlFileImport}>
            <Form>
                <div className={`w-full p-2 border-b flex flex-row items-center bg-sidemenu-focus text-white justify-between`}>
                    <div className="flex flex-row">
                        <span className="pl-3 font-bold font text-white">{translate.byKey(TranslationKeys.crlImport)}</span>
                    </div>
                    <img src={closeIcon} data-testid="modal_closeAbout" onClick={onClose} className="cursor-pointer mr-2" />
                </div>
                <ImportCrlContent/>
            </Form>
        </Formik>
    </Modal>
}

export default ImportCrlModal;
